/**
 * Shared email and phone checks for clinic contacts and patient records.
 *
 * Empty values stay valid: both fields are optional. A non-empty value must
 * pass before the form, the contact mutation, or create/update-patient saves it.
 * Contacts and patient records call the same functions so correcting one field
 * never clears an error on the other.
 */

export type ContactFieldName = 'email' | 'phone';

export type ContactFieldAssessment = {
  email: boolean;
  phone: boolean;
};

export type ContactFieldMessages = {
  invalidEmail: string;
  invalidPhone: string;
};

export type ContactFieldErrorTexts = {
  canSave: boolean;
  email?: string;
  phone?: string;
};

const EMAIL_PATTERN =
  /^[a-z0-9](?:[a-z0-9._%+-]*[a-z0-9])?@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+(?:[a-z]{2,}|xn--[a-z0-9]+)$/i;

const PHONE_CHARACTERS = /^[0-9+\-().\s]+$/;

export function isValidContactEmail(value: string | null | undefined): boolean {
  if (value == null) return true;
  const trimmed = value.trim();
  if (trimmed.length === 0) return true;
  if (trimmed.length > 254) return false;
  if (trimmed.includes('..')) return false;
  return EMAIL_PATTERN.test(trimmed);
}

/**
 * Danish national numbers are 8 digits and do not start with 0.
 * The same number may include the 45 country code, with or without a leading +.
 * Other countries are accepted as E.164 (+ or 00), 8–15 digits, not starting with 0.
 * Spaces, dashes, dots, and parentheses are formatting only.
 */
export function isValidContactPhone(value: string | null | undefined): boolean {
  if (value == null) return true;
  const trimmed = value.trim();
  if (trimmed.length === 0) return true;
  if (!PHONE_CHARACTERS.test(trimmed)) return false;
  if (trimmed.includes('+') && !trimmed.startsWith('+')) return false;
  if ((trimmed.match(/\+/g) ?? []).length > 1) return false;

  const compact = trimmed.replace(/[\s\-().]/g, '');
  const international = compact.startsWith('+') || compact.startsWith('00');
  const digits = compact.startsWith('+')
    ? compact.slice(1)
    : compact.startsWith('00')
      ? compact.slice(2)
      : compact;

  if (!/^\d+$/.test(digits)) return false;

  if (international) {
    if (digits.startsWith('0')) return false;
    if (digits.startsWith('45')) {
      const subscriber = digits.slice(2);
      return subscriber.length === 8 && !subscriber.startsWith('0');
    }
    return digits.length >= 8 && digits.length <= 15;
  }

  if (digits.length === 8) return !digits.startsWith('0');
  if (digits.length === 10 && digits.startsWith('45')) {
    return !digits.slice(2).startsWith('0');
  }
  return false;
}

export function assessContactFields(
  email: string | null | undefined,
  phone: string | null | undefined,
): ContactFieldAssessment {
  return {
    email: isValidContactEmail(email),
    phone: isValidContactPhone(phone),
  };
}

/** Patient create/edit uses the same rules as clinic contacts. */
export function assessPatientContactFields(
  email: string | null | undefined,
  phone: string | null | undefined,
): ContactFieldAssessment {
  return assessContactFields(email, phone);
}

export function invalidContactFields(
  email: string | null | undefined,
  phone: string | null | undefined,
): ContactFieldName[] {
  const assessment = assessContactFields(email, phone);
  const fields: ContactFieldName[] = [];
  if (!assessment.email) fields.push('email');
  if (!assessment.phone) fields.push('phone');
  return fields;
}

export class InvalidContactFieldError extends Error {
  readonly fields: ContactFieldName[];

  constructor(fields: ContactFieldName[]) {
    super(fields.join(','));
    this.name = 'InvalidContactFieldError';
    this.fields = fields;
  }
}

export function assertValidContactFields(
  email: string | null | undefined,
  phone: string | null | undefined,
): void {
  const fields = invalidContactFields(email, phone);
  if (fields.length > 0) throw new InvalidContactFieldError(fields);
}

/**
 * Field messages stay hidden until the user tries to save. After that, each
 * field is assessed on its own, so a corrected email does not hide a phone error.
 */
export function contactFieldErrorTexts(
  submitted: boolean,
  email: string | null | undefined,
  phone: string | null | undefined,
  messages: ContactFieldMessages,
): ContactFieldErrorTexts {
  const assessment = assessContactFields(email, phone);
  const canSave = assessment.email && assessment.phone;
  if (!submitted || canSave) return { canSave };
  return {
    canSave,
    email: assessment.email ? undefined : messages.invalidEmail,
    phone: assessment.phone ? undefined : messages.invalidPhone,
  };
}
